/**
 * New releases, which the holder is made to take.
 *
 * The browser looks for a new `sw.js` when a page of the application is
 * opened, and an installed application that comes back from the background is
 * not opened again: it resumes. A phone that lives with the application in
 * its recent apps could stay on an old release for days. So the application
 * asks itself, on every launch, every time it comes back to the front and
 * every quarter of an hour while it stays there.
 *
 * When the browser finds a release, the application stops: a screen over
 * everything says there is a new version and shows its download, file by
 * file, from the worker's own count (`sw.ts`). Nothing behind it can be
 * reached. When the new worker takes over, the page reloads into the new
 * release. A download that fails leaves the screen up with a retry, because
 * continuing on the old release is the one thing this screen exists to stop.
 *
 * Not on the first install: a page with no worker in control is the
 * installer's business, and it shows the same download in its own first step.
 */

const INSTALLING =
  "Se está instalando. La aplicación se abre sola en la versión nueva cuando termine.";
const STUCK = "Hay que terminar de instalarla para seguir usando la aplicación.";

/** How often a page left in front asks for a release. */
const CHECK_EVERY_MS = 15 * 60 * 1000;

// Two signals say the new release took over, the new worker reaching
// `activated` and `controllerchange`, and they come close together. One
// reload is enough; a second would restart the first one's navigation.
let reloading = false;
const reloadOnce = () => {
  if (reloading) return;
  reloading = true;
  location.reload();
};

interface Progress {
  t: "progress";
  done: number;
  total: number;
  bytes: number;
  complete: boolean;
  build?: string;
}

export function watchForUpdates(registration: ServiceWorkerRegistration): void {
  let screen: UpdateScreen | null = null;

  const block = (worker: ServiceWorker) => {
    screen ??= new UpdateScreen(() => void registration.update().catch(() => screen?.failed()));
    screen.follow(worker);
  };

  registration.addEventListener("updatefound", () => {
    if (registration.installing && navigator.serviceWorker.controller) {
      block(registration.installing);
    }
  });
  // Found by the `register` call that just returned, before this listener.
  if (registration.installing && navigator.serviceWorker.controller) {
    block(registration.installing);
  }
  // A release fast enough to install and take over before any of the above
  // saw it still reloads the page the old one served. Only when a worker was
  // already in control: the first install also fires `controllerchange`, and
  // reloading there would restart an enrolment for nothing.
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, {
      once: true,
    });
  }

  const check = () => void registration.update().catch(() => {});
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  setInterval(check, CHECK_EVERY_MS);
  // Back online with a failed download on screen: try again without a tap.
  window.addEventListener("online", () => {
    if (screen) check();
  });
}

class UpdateScreen {
  private readonly node: HTMLElement;
  private readonly lead: HTMLElement;
  private readonly status: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly retry: HTMLButtonElement;
  // Set once a download failed: the worker's last progress messages can
  // arrive after it went redundant, and must not cover the failure up.
  private stopped = false;

  constructor(onRetry: () => void) {
    this.node = document.createElement("div");
    this.node.id = "update";
    this.node.setAttribute("role", "alertdialog");
    this.node.setAttribute("aria-modal", "true");
    this.node.setAttribute("aria-labelledby", "update-title");
    this.node.innerHTML = `
      <div class="uhb">Universidad de La Habana</div>
      <img class="appicon" src="/icon-192.png" alt="">
      <h1 id="update-title">Hay una versión nueva</h1>
      <p id="update-lead">${INSTALLING}</p>
      <div class="pbar" id="update-bar"><i></i></div>
      <p class="sub" id="update-status" aria-live="polite">Preparando la descarga…</p>
      <div class="foot"><button id="update-retry" hidden>Reintentar</button></div>`;
    document.body.append(this.node);
    // Whatever was being done behind it stops being reachable: no focus, no
    // taps, no screen reader wandering into the old release.
    for (const element of [document.querySelector("#app"), document.querySelector("#bar")]) {
      element?.setAttribute("inert", "");
    }
    this.lead = this.node.querySelector("#update-lead")!;
    this.status = this.node.querySelector("#update-status")!;
    this.fill = this.node.querySelector<HTMLElement>("#update-bar i")!;
    this.retry = this.node.querySelector("#update-retry")!;
    this.retry.addEventListener("click", () => {
      this.retry.hidden = true;
      this.status.textContent = "Buscando la versión nueva…";
      onRetry();
    });
    navigator.serviceWorker.addEventListener("message", (event) => {
      const progress = event.data as Progress | null;
      if (progress?.t === "progress" && progress.total > 0 && !progress.complete && !this.stopped) {
        this.fill.style.width = `${Math.round((100 * progress.done) / progress.total)}%`;
        this.status.textContent = `${progress.done} de ${progress.total} archivos`;
      }
    });
    navigator.serviceWorker.startMessages();
  }

  follow(worker: ServiceWorker): void {
    this.stopped = false;
    this.lead.textContent = INSTALLING;
    this.retry.hidden = true;
    this.fill.style.width = "0";
    this.status.textContent = "Descargando…";
    const watch = () => {
      if (worker.state === "installed" || worker.state === "activating") {
        this.fill.style.width = "100%";
        this.status.textContent = "Abriendo la versión nueva…";
      } else if (worker.state === "activated") {
        reloadOnce();
      } else if (worker.state === "redundant") {
        this.failed();
      }
    };
    worker.addEventListener("statechange", watch);
    watch();
  }

  failed(): void {
    this.stopped = true;
    this.lead.textContent = STUCK;
    this.status.textContent =
      "No se pudo descargar la versión nueva. Hace falta conexión para terminarla.";
    this.retry.hidden = false;
  }
}

/** The running release's build, as the worker in control names its cache;
 *  null with no worker, or one that does not answer within a second. */
export function runningBuild(): Promise<string | null> {
  const worker = navigator.serviceWorker?.controller;
  if (!worker) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => done(null), 1000);
    const onMessage = (event: MessageEvent) => {
      const build = (event.data as Progress | null)?.build;
      if (build) done(build);
    };
    const done = (build: string | null) => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("message", onMessage);
      resolve(build);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    navigator.serviceWorker.startMessages();
    worker.postMessage({ t: "status" });
  });
}
